import { RefundExternalKeysFilter } from 'app/src/features/Shopping/Refund/components/functions'
import { RefundConfirmation } from 'app/src/features/Shopping/Refund/components/RefundConfirmation'
import { RefundFileUploader } from 'app/src/features/Shopping/Refund/components/RefundFileUploader'
import {
  AdditionalDocumentUploadStepName,
  ConfirmationStepName,
  DocumentScope,
  InvoiceUpdateStepName,
  InvoiceUploadStepName,
  onFileLoaded,
  refundForwardAction,
  setRefundBulkFormData,
  setRefundFormData,
  SuccessFinalStepName,
  updateFiles,
} from 'app/src/features/Shopping/Refund/components/RefundFunctions'
import { RefundInvoiceForm } from 'app/src/features/Shopping/Refund/components/RefundInvoiceForm'
import { RefundSuccess } from 'app/src/features/Shopping/Refund/components/RefundSuccess'
import {
  fileStatusChecker,
  REGEX_Importo,
  type FileStepType,
  type FormComponentInfoType,
  type FormItemEnum,
  type StatusCheckerInfoBundleType,
  type WizardStepConfigType,
} from 'app/src/features/Shopping/shared'
import { DefaultCFRegex, type DocumentFileType } from 'app/src/model'
import { refundApi, shoppingApi } from 'app/src/services/welfareportal'
import { BackToCatalogButton, ErrorPage, PageWidget, PortalConfirmDialog, PortalDialog } from 'app/src/shared/components'
import { SafetyThresholdBox } from 'app/src/shared/components/SafetyThresholdBox'
import { decimalFormatter } from 'app/src/shared/helpers'
import { useWebAppContext, useWelfarePortalNavigate } from 'app/src/shared/hooks'
import { cloneDeep } from 'lodash'
import { Button } from 'primereact/button'
import { ProgressBar } from 'primereact/progressbar'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { date, number, object, ref, string, type ObjectShape } from 'yup'

export function RefundFunnel(props: { refund: refundApi.Refund }) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Refund' })
  const navigate = useWelfarePortalNavigate()
  const context = useWebAppContext()
  const confirmRefundMutation = refundApi.useRefundRequestConfirm()
  const cancelRefundMutation = refundApi.useRefundRequestDelete()
  const creditApiResponse = shoppingApi.useCreditGet(
    {},
    {
      meta: {
        skip404Errors: true,
      },
    },
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [formData, setFormData] = useState<any[]>()

  const [showAbortRefundModal, setShowAbortRefundModal] = useState<boolean>(false)
  const [showConfirmRefundModal, setShowConfirmRefundModal] = useState<boolean>(false)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [formErrors, setFormErrors] = useState<any>()
  const [forwardButtonDisabled, setForwardButtonDisabled] = useState<boolean>(false)
  const [forwardActionRequested, setForwardActionRequested] = useState<boolean>(false)
  const [additionalRadioKeys, setAdditionalRadioKeys] = useState<string[]>()
  const [privacyStatementAccept, setPrivacyStatementAccept] = useState<boolean>(false)
  const [showAdditionalDocumentsModal, setShowAdditionalDocumentsModal] = useState<boolean>(false)

  const [genericError, setGenericError] = useState<JSX.Element | string>()

  const [files, setFiles] = useState<FileStepType[]>()
  const wizardSteps = useMemo(() => {
    const steps: WizardStepConfigType[] = []
    const invoiceDocument = props.refund.rule?.documents.find((x) => x.types.some((y) => y === 'invoice'))
    const additionalDocuments = props.refund.rule?.documents?.find((x) => x.types.some((y) => y === 'none'))
    let yupSchema

    const invoiceYupShape: Record<string, unknown> = {
      invoiceId: string().required(t('Errors.Required.invoiceId')),
      invoiceDate: date().required(t('Errors.Required.invoiceDate')).max(new Date(), t('Errors.Max.invoiceDate')),
      vendorName: string().required(t('Errors.Required.vendorName')),
      vendorTaxId: string().required(t('Errors.Required.vendorTaxId')),
      invoiceTotal: string().required(t('Errors.Required.invoiceTotal')).matches(REGEX_Importo, t('Errors.Matches.invoiceTotal')),
      requestedRefund: string()
        .required(t('Errors.Required.requestedRefund'))
        .matches(REGEX_Importo, t('Errors.Matches.requestedRefund')),
      invoiceTotalNumber: number().required(t('Errors.Required.invoiceTotal')).min(0.01, t('Errors.Min.invoiceTotal')),
      requestedRefundNumber: number()
        .required(t('Errors.Required.requestedRefund'))
        .min(0.01, t('Errors.Min.requestedRefund'))
        .max(ref('invoiceTotalNumber'), t('Errors.Max.requestedRefund')),
      welfareCreditNumber: number()
        .required(t('Errors.Required.welfareCredit'))
        .min(ref('requestedRefundNumber'), t('Errors.Min.welfareCredit')),
    }

    if (
      props.refund.rule?.externalKeys &&
      props.refund.rule.externalKeys.some((key) => RefundExternalKeysFilter(key, props.refund))
    ) {
      const externalKeys: string[] = []
      let externalKeysLabel = ''
      props.refund.rule.externalKeys
        .filter((key) => RefundExternalKeysFilter(key, props.refund))
        .forEach((externalKey, index) => {
          console.log('externalKey', externalKey, 'index', index)
          if (externalKey.format === 'number') {
            externalKeys.push(externalKey.key + 'Number')

            if (index > 0) externalKeysLabel += ','

            externalKeysLabel += externalKey.label
            invoiceYupShape[externalKey.key + 'Number'] = number().required(
              t('Errors.Required.customExternalKey', { KEY: externalKey.key }),
            )
            invoiceYupShape[externalKey?.key ?? ''] = string()
              .required(t('Errors.Required.customExternalKey', { KEY: externalKey.key }))
              .matches(REGEX_Importo, t('Errors.Matches.customExternalKey'))
          } else if (externalKey.format === 'string') {
            invoiceYupShape[externalKey?.key ?? ''] = string()
              .required(t('Errors.Required.customExternalKey', { KEY: externalKey.label }))
              .matches(DefaultCFRegex, t('Errors.Matches.fiscalCode'))
          }
        })

      yupSchema = object()
        .shape(invoiceYupShape as ObjectShape)
        .test({
          message: t('Errors.Custom.customExternalKey', { LABEL: externalKeysLabel }),
          name: 'externalKeysSum',
          test: ({ ...data }) => {
            let sum: number = data.requestedRefundNumber as number
            if (externalKeys.some((x) => !data[x])) return true

            externalKeys.forEach((externalKey) => (sum += data[externalKey] as number))
            return sum <= (data.invoiceTotalNumber as number)
          },
        })
    } else {
      yupSchema = object().shape(invoiceYupShape as ObjectShape)
    }

    if (invoiceDocument) {
      const invoiceStep: WizardStepConfigType = {
        documentAttributes: {
          documentScope: DocumentScope,
          documentExtractionType: invoiceDocument.extractionType ?? invoiceDocument.types[0],
          max: 1,
          min: 1,
        },
        stepName: InvoiceUploadStepName,
        title: t('Funnel.Steps.InvoiceUpload.title', { REFUNDCATEGORY: props.refund.category }),
        descriptionPrefix: <div dangerouslySetInnerHTML={{ __html: t('Funnel.Steps.InvoiceUpload.descriptionPrefix') }} />,
        description: invoiceDocument?.description,
        statusChecker: fileStatusChecker,
      }

      const invoiceUpdateStep: WizardStepConfigType = {
        title: t('Funnel.Steps.InvoiceUpdate.title', { REFUNDCATEGORY: props.refund.category }),
        descriptionPrefix: <div dangerouslySetInnerHTML={{ __html: t('Funnel.Steps.InvoiceUpdate.descriptionPrefix') }} />,
        description: t('Funnel.Steps.InvoiceUpdate.description'),
        canAbortRequest: true,
        stepName: InvoiceUpdateStepName,
        formComponentInfo: {
          fileStepName: InvoiceUploadStepName,
          documentType: invoiceDocument.extractionType ?? '',
          yupSchema,
          initializeForm: (file: DocumentFileType, credit_: number) => {
            const invoiceTotal = file?.documentDetails?.data?.invoiceTotal?.value
              ? decimalFormatter.format(file.documentDetails.data.invoiceTotal.value)
              : ''

            const retObj: Record<string, unknown> = {
              invoiceId: file?.documentDetails?.data?.invoiceId?.value,
              invoiceDate: file?.documentDetails?.data?.invoiceDate?.value,
              vendorName: file?.documentDetails?.data?.vendorName?.value,
              vendorTaxId: file?.documentDetails?.data?.vendorTaxId?.value,
              invoiceTotal,
              invoiceTotalNumber: file?.documentDetails?.data?.invoiceTotal?.value ?? 0,
              requestedRefundNumber: file?.documentDetails?.data?.invoiceTotal?.value ?? 0,
              requestedRefund: invoiceTotal ?? '0',
              welfareCreditNumber: credit_,
            }

            if (
              props.refund.rule?.externalKeys?.some((key: refundApi.AdditionalKey) => RefundExternalKeysFilter(key, props.refund))
            ) {
              props.refund.rule.externalKeys
                .filter((key: refundApi.AdditionalKey) => RefundExternalKeysFilter(key, props.refund))
                .forEach((externalKey: refundApi.AdditionalKey) => {
                  const fieldValue = file?.documentDetails?.data?.[externalKey.key ?? '']?.value
                  if (fieldValue) {
                    retObj[externalKey.key + 'Number'] = fieldValue
                    retObj[externalKey.key ?? ''] =
                      externalKey.format === 'number' ? decimalFormatter.format(fieldValue) : fieldValue

                    if (invoiceTotal && externalKey.format === 'number') {
                      ;(retObj.requestedRefundNumber as number) -= fieldValue
                    }
                  }
                })

              retObj.requestedRefund = retObj.requestedRefundNumber
                ? decimalFormatter.format(retObj.requestedRefundNumber as number)
                : '0'
            }

            return retObj
          },
        },
      }
      steps.push(...[invoiceStep, invoiceUpdateStep])

      if (additionalDocuments) {
        steps.push({
          documentAttributes: {
            documentScope: DocumentScope,
            documentExtractionType: additionalDocuments.extractionType ?? additionalDocuments.types[0],
            max: additionalDocuments.max,
            min: additionalDocuments.min,
          },
          stepName: AdditionalDocumentUploadStepName,
          canAbortRequest: true,
          title: t('Funnel.Steps.AdditionalDocumentsUpload.title', { REFUNDCATEGORY: props.refund.category }),
          description: additionalDocuments?.description,
          descriptionModal: additionalDocuments?.descriptionModal,
          statusChecker: fileStatusChecker,
        })
      }

      steps.push(
        {
          stepName: ConfirmationStepName,
          canAbortRequest: true,
          title: t('Funnel.Steps.ConfirmationStep.title', { REFUNDCATEGORY: props.refund.category }),
          statusChecker: (_step: WizardStepConfigType, checkerBundleInfo: StatusCheckerInfoBundleType) =>
            (checkerBundleInfo.privacyStatementAccept || typeof props.refund?.rule?.termsAndConditions === 'undefined') &&
            (!props?.refund?.rule?.additionalAttributes?.radio ||
              props?.refund?.rule?.additionalAttributes?.radio.length ===
                checkerBundleInfo?.additionalRadioKeys?.filter((x) => typeof x !== 'undefined').length),
          confirmationStep: true,
        },
        {
          stepName: SuccessFinalStepName,
        },
      )

      return steps
    }
    return []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.refund])

  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0)

  useEffect(() => {
    if (wizardSteps) {
      setFormData(wizardSteps.map(() => undefined))
      setFiles(wizardSteps.map((x) => ({ stepName: x.stepName, files: [] })))
    }
  }, [wizardSteps])

  const currentStep = useMemo(() => {
    return wizardSteps?.length > 0 && currentStepIndex >= 0 && wizardSteps?.[currentStepIndex]
      ? wizardSteps[currentStepIndex]
      : undefined
  }, [currentStepIndex, wizardSteps])

  const credit = useMemo(() => {
    const limitType = props.refund?.attributes?.['limitType'] ?? ''
    return !limitType ||
      creditApiResponse.isError ||
      !creditApiResponse?.data?.creditByServiceCategory ||
      !creditApiResponse?.data?.creditByServiceCategory[limitType]
      ? 0
      : (creditApiResponse?.data?.creditByServiceCategory[limitType].available ?? 0)
  }, [creditApiResponse, props.refund])

  const initializeForm = useCallback(
    (formComponentInfo: FormComponentInfoType) => {
      const fileEntry = files?.find((x) => x.stepName === formComponentInfo.fileStepName)
      if (!fileEntry || !fileEntry.files) return {}
      else {
        const docFile = fileEntry.files.find((x) => x.documentType === formComponentInfo.documentType)
        if (!docFile) return {}
        else return formComponentInfo.initializeForm(docFile, credit)
      }
    },
    [credit, files],
  )
  const getTargetComponentFormFile = useCallback(
    (_files: FileStepType[] | undefined, formComponent: FormComponentInfoType) =>
      _files
        ?.find((x) => formComponent.fileStepName === x.stepName)
        ?.files.find((x) => x.documentType === formComponent.documentType),
    [],
  )

  useEffect(() => {
    if (forwardActionRequested) {
      setForwardButtonDisabled(true)
      return
    }

    const currentStep_ =
      wizardSteps?.length > 0 && currentStepIndex >= 0 && wizardSteps?.[currentStepIndex]
        ? wizardSteps[currentStepIndex]
        : undefined

    if (currentStep_) {
      setForwardButtonDisabled(
        currentStep_.statusChecker
          ? !currentStep_.statusChecker(currentStep_, {
              wizardSteps,
              filesToCheck: files!,
              privacyStatementAccept:
                privacyStatementAccept ||
                typeof props.refund.rule?.termsAndConditions === 'undefined' ||
                !props.refund.rule?.termsAndConditions,
              additionalRadioKeys,
              formData: formData!,
            })
          : false,
      )
    } else setForwardButtonDisabled(false)
  }, [
    additionalRadioKeys,
    currentStepIndex,
    props.refund.rule?.termsAndConditions,
    files,
    formData,
    forwardActionRequested,
    privacyStatementAccept,
    wizardSteps,
  ])

  useEffect(() => {
    if (forwardActionRequested) {
      refundForwardAction(
        context,
        confirmRefundMutation,
        wizardSteps,
        currentStep!,
        currentStepIndex,
        setCurrentStepIndex,
        formData!,
        props.refund,
        initializeForm,
        setFormErrors,
        setForwardActionRequested,
        setFormData,
        files!,
        setFiles,
        getTargetComponentFormFile,
        additionalRadioKeys!,
        setGenericError,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forwardActionRequested])

  const showHeader = useMemo(() => {
    return (
      !genericError &&
      wizardSteps &&
      wizardSteps.length > 0 &&
      currentStep?.stepName !== wizardSteps[wizardSteps?.length - 1].stepName
    )
  }, [currentStep?.stepName, genericError, wizardSteps])

  const onGoBackButton = useCallback(() => {
    if (currentStepIndex > 0) setCurrentStepIndex(currentStepIndex - 1)
    else setShowAbortRefundModal(true)
  }, [currentStepIndex])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentStepIndex])

  useEffect(() => {
    if (confirmRefundMutation.isPending) window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [confirmRefundMutation.isPending])
  const shouldDisplayFringeWarning = useMemo(() => {
    const config = context.userInfo?.welfareCampaign?.configuration
    const welfareCredits = creditApiResponse.data?.creditByServiceCategory?.welfareArt51
    const fringeAssigned = creditApiResponse.data?.fringeAssigned

    if (!config?.fringeWarningThreshold || !welfareCredits) return false

    const { total, available } = welfareCredits || {}
    if (total === undefined || available === undefined || fringeAssigned === undefined) return false

    const requestedRefundNumber = formData?.[currentStepIndex]?.requestedRefundNumber || 0
    const thresholdRatio = config.fringeWarningThreshold / 100
    const used = total - available
    const percUsed = (used + requestedRefundNumber) / fringeAssigned

    return percUsed >= thresholdRatio
  }, [context.userInfo.welfareCampaign.configuration, creditApiResponse.data, currentStepIndex, formData])

  const [confirmBtnEnabled, setConfirmBtnEnabled] = useState(false)

  return (
    <PageWidget>
      {!confirmRefundMutation.isPending ? (
        <>
          {showAbortRefundModal && (
            <PortalConfirmDialog
              accept={async () => {
                try {
                  await cancelRefundMutation.mutateAsync({ pathParams: { refundId: props.refund.refundId } })
                } catch (error: unknown) {
                  context.logger?.error('Error Deleting Refund', { refundId: props.refund.refundId, error })
                } finally {
                  setShowAbortRefundModal(false)
                  navigate.goToCatalog()
                }
              }}
              reject={() => setShowAbortRefundModal(false)}
              body={<div dangerouslySetInnerHTML={{ __html: t('Funnel.Dialogs.Abort.body') }} />}
              title={t('Funnel.Dialogs.Abort.title')}
            />
          )}
          {showConfirmRefundModal && (
            <PortalConfirmDialog
              accept={() => {
                setForwardActionRequested(true)
              }}
              reject={() => setShowConfirmRefundModal(false)}
              yesLoading={confirmRefundMutation?.isPending}
              body={<div dangerouslySetInnerHTML={{ __html: t('Funnel.Dialogs.Confirm.body') }} />}
              title={t('Funnel.Dialogs.Confirm.title')}
            />
          )}
          {showAdditionalDocumentsModal && currentStep?.descriptionModal && (
            <PortalDialog
              onHide={() => setShowAdditionalDocumentsModal(false)}
              headerProps={{ title: t('Funnel.Dialogs.AdditionalDocuments.title') }}
              footerProps={{
                okLabel: t('Funnel.Dialogs.AdditionalDocuments.okButtonLabel'),
                okCallBack: () => setShowAdditionalDocumentsModal(false),
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: currentStep.descriptionModal }} />
            </PortalDialog>
          )}
          {showHeader && (
            <Button link onClick={onGoBackButton} icon='pi pi-arrow-left' pt={{ root: { className: 'mb-2' } }}>
              <span className='ml-2 font-bold'>
                {currentStepIndex === 0 ? t('Funnel.abortRequestBtnLabel') : t('Funnel.goBackBtnLabel')}
              </span>
            </Button>
          )}
          {!genericError && showHeader && (
            <div>
              <p className='mb-2 font-bold'>
                {t('Funnel.wizardTitle', { STEPNUMBER: currentStepIndex + 1, STEPCOUNT: wizardSteps?.length - 1 })}
              </p>

              <ProgressBar
                value={((currentStepIndex + 1) / (wizardSteps.length - 1)) * 100}
                showValue={false}
                className='h-[10px] rounded-none'
                pt={{
                  root: {
                    'aria-label': `Progresso: passo ${currentStepIndex + 1} di ${wizardSteps?.length - 1}`,
                    role: 'progressbar',
                  },
                }}
              />
            </div>
          )}
          {genericError && (
            <ErrorPage>
              <div className='mx-auto mt-3'>{genericError}</div>
              <div className='mx-auto flex justify-center'>
                <BackToCatalogButton />
              </div>
            </ErrorPage>
          )}

          {currentStep && !genericError && (
            <>
              {currentStep.title && (
                <div className='my-4 text-2xl font-bold' dangerouslySetInnerHTML={{ __html: currentStep.title }} />
              )}
              {currentStep.descriptionPrefix && <div className='mb-4 mt-4'>{currentStep.descriptionPrefix}</div>}
              {currentStep.description && (
                <div className='mb-4 mt-4' dangerouslySetInnerHTML={{ __html: `<div>${currentStep.description}</div>` }}></div>
              )}
              {currentStep.descriptionModal && (
                <div>
                  <span className='text-base'>
                    {t('Funnel.Dialogs.AdditionalDocuments.requestButtonLabelPrefix')}
                    <Button
                      role='link'
                      text
                      pt={{ root: { className: 'underline pb-0  pl-0 ml-2' } }}
                      link
                      onClick={() => setShowAdditionalDocumentsModal(true)}
                    >
                      {t('Funnel.Dialogs.AdditionalDocuments.requestButtonLabel')}
                    </Button>
                  </span>
                </div>
              )}
              {currentStep.documentAttributes && (
                <RefundFileUploader
                  refundId={props.refund.refundId}
                  onDeletedFile={(documentId: string) =>
                    updateFiles(
                      files!,
                      setFiles,
                      currentStep.stepName,
                      files
                        ?.find((x) => x.stepName === currentStep?.stepName)
                        ?.files?.filter((x) => x.documentId !== documentId) ?? [],
                    )
                  }
                  onFileLoaded={(file: DocumentFileType) =>
                    onFileLoaded(wizardSteps, currentStep, files!, setFiles, file, formData!, setFormData)
                  }
                  files={files?.find((x) => x.stepName === currentStep.stepName)?.files ?? []}
                  documentAttributes={currentStep?.documentAttributes}
                />
              )}
              {currentStep.formComponentInfo && (
                <div>
                  <RefundInvoiceForm
                    file={getTargetComponentFormFile(files, currentStep?.formComponentInfo)}
                    refund={props.refund}
                    formData={formData?.[currentStepIndex] ?? initializeForm(currentStep.formComponentInfo)}
                    formErrors={formErrors}
                    setFormData={(fieldId: string, fieldValue: unknown, type: FormItemEnum) =>
                      setRefundFormData(
                        currentStep,
                        currentStepIndex,
                        formData!,
                        initializeForm,
                        setFormData,
                        fieldId,
                        fieldValue,
                        type,
                      )
                    }
                    resetForm={() => {
                      const formData_ = cloneDeep(formData)
                      if (currentStep.formComponentInfo && formData_)
                        formData_[currentStepIndex] = initializeForm(currentStep.formComponentInfo)
                      setFormData(formData_)
                    }}
                    setBulkFormData={(fieldIds: string[], fieldValues: unknown[], types: FormItemEnum[]) =>
                      setRefundBulkFormData(
                        currentStep,
                        currentStepIndex,
                        formData!,
                        initializeForm,
                        setFormData,
                        fieldIds,
                        fieldValues,
                        types,
                      )
                    }
                  />
                  <div>
                    {
                      // box fringe
                      context.userInfo.welfareCampaign.configuration.fringeWarningThreshold && shouldDisplayFringeWarning ? (
                        <div className='mt-8'>
                          <SafetyThresholdBox
                            threshold={context.userInfo.welfareCampaign.configuration.fringeWarningThreshold}
                            onChange={function (value: boolean): void {
                              setConfirmBtnEnabled(value)
                            }}
                          />
                        </div>
                      ) : (
                        <></>
                      )
                    }
                  </div>
                </div>
              )}
              {typeof currentStep?.confirmationStep !== 'undefined' && currentStep?.confirmationStep && (
                <RefundConfirmation
                  refund={props.refund}
                  privacyStatementAccept={privacyStatementAccept}
                  setPrivacyStatementAccept={setPrivacyStatementAccept}
                  additionalRadioKeys={additionalRadioKeys}
                  setAdditionalRadioKeys={(radioGroupIndex: number, selectedKey: string) => {
                    const additionalRadioKeysCloned = cloneDeep(additionalRadioKeys) ?? []
                    additionalRadioKeysCloned[radioGroupIndex] = selectedKey
                    setAdditionalRadioKeys(additionalRadioKeysCloned)
                  }}
                  formData={{
                    requestedRefund:
                      formData?.[wizardSteps?.findIndex((x) => x.stepName === InvoiceUpdateStepName)].requestedRefundNumber,
                    documentDate: formData?.[wizardSteps?.findIndex((x) => x.stepName === InvoiceUpdateStepName)].invoiceDate,
                    invoiceId: formData?.[wizardSteps?.findIndex((x) => x.stepName === InvoiceUpdateStepName)].invoiceId,
                    vendorName: formData?.[wizardSteps?.findIndex((x) => x.stepName === InvoiceUpdateStepName)].vendorName,
                    vendorTaxId: formData?.[wizardSteps?.findIndex((x) => x.stepName === InvoiceUpdateStepName)].vendorTaxId,
                    invoiceTotal:
                      formData?.[wizardSteps?.findIndex((x) => x.stepName === InvoiceUpdateStepName)].invoiceTotalNumber,
                    customerTaxId: formData?.[wizardSteps?.findIndex((x) => x.stepName === InvoiceUpdateStepName)].customerTaxId,
                  }}
                />
              )}
              {currentStep?.stepName === wizardSteps[wizardSteps?.length - 1].stepName && <RefundSuccess />}
              {currentStepIndex < wizardSteps?.length - 1 && (
                <div className='mt-8 flex justify-center'>
                  <Button
                    disabled={
                      forwardButtonDisabled ||
                      (currentStep.stepName === 'InvoiceUpdate' && shouldDisplayFringeWarning && !confirmBtnEnabled)
                    }
                    onClick={() => {
                      if (currentStep.stepName === 'InvoiceUpdate')
                        localStorage.setItem('fringeThresholdAccept', confirmBtnEnabled ? confirmBtnEnabled.toString() : 'false')
                      if (typeof currentStep.confirmationStep !== 'undefined' && currentStep.confirmationStep)
                        setShowConfirmRefundModal(true)
                      else setForwardActionRequested(true)
                    }}
                  >
                    {currentStep.confirmationStep ? t('Funnel.confirmBtnLabel') : t('Funnel.continueBtnLabel')}
                  </Button>
                </div>
              )}
              {typeof currentStep?.canAbortRequest !== 'undefined' && currentStep?.canAbortRequest && (
                <div className='mt-8 flex justify-center'>
                  <Button onClick={() => setShowAbortRefundModal(true)} link>
                    <b>{t('Funnel.abortRequestBtnLabel')}</b>
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <div className='mt-2 flex'>
          <div className='mx-auto'>
            <ProgressSpinner
              data-testid='loading-element'
              className='flex'
              pt={{
                root: {
                  'aria-label': 'Caricamento in corso',
                  role: 'progressbar',
                },
              }}
            />
            <div className='mt-4' dangerouslySetInnerHTML={{ __html: t('Funnel.requestInProgressDescription') }}></div>
          </div>
        </div>
      )}
    </PageWidget>
  )
}
